import React, { useEffect, useContext, useMemo, useRef, createContext } from 'react';
import isEmpty from 'lodash/isEmpty';

const EVENT_TYPES = ['mousedown', 'touchstart'];
function isClickAway(target, refs) {
  // The target element is not contained in any of the listed element references.
  return !refs.some(e => {
    var _e$current;
    return e === null || e === void 0 ? void 0 : (_e$current = e.current) === null || _e$current === void 0 ? void 0 : _e$current.contains(target);
  });
}
/**
 * Listen to clicks away from the given elements and callback the passed in function.
 *
 * Warning: If you need to detect click away on nested React portals, please use the `ClickAwayProvider` component.
 */
function useClickAway(_ref) {
  let {
    callback,
    childrenRefs
  } = _ref;
  useEffect(() => {
    const {
      current: currentRefs
    } = childrenRefs;
    if (!callback || !currentRefs || isEmpty(currentRefs)) {
      return undefined;
    }
    const listener = evt => {
      if (isClickAway(evt.target, currentRefs)) {
        callback(evt);
      }
    };
    EVENT_TYPES.forEach(evtType => document.addEventListener(evtType, listener));
    return () => {
      EVENT_TYPES.forEach(evtType => document.removeEventListener(evtType, listener));
    };
  }, [callback, childrenRefs]);
}

const ClickAwayAncestorContext = /*#__PURE__*/createContext(null);
/**
 * Component combining the `useClickAway` hook with a React context to hook into the React component tree and make sure
 * we take into account both the DOM tree and the React tree to detect click away.
 *
 * @return the react component.
 */
const ClickAwayProvider = _ref => {
  let {
    children,
    callback,
    childrenRefs,
    parentRef
  } = _ref;
  const parentContext = useContext(ClickAwayAncestorContext);
  const currentContext = useMemo(() => {
    const context = {
      childrenRefs: [],
      /**
       * Add element refs to the current context and propagate to the parent context.
       */
      addRefs() {
        // Add element refs that should be considered as inside the click away context.
        context.childrenRefs.push(...arguments);
        if (parentContext) {
          // Also add then to the parent context
          parentContext.addRefs(...arguments);
          if (parentRef) {
            // The parent element is also considered as inside the parent click away context but not inside the current context
            parentContext.addRefs(parentRef);
          }
        }
      }
    };
    return context;
  }, [parentContext, parentRef]);
  useEffect(() => {
    const {
      current: currentRefs
    } = childrenRefs;
    if (!currentRefs) {
      return;
    }
    currentContext.addRefs(...currentRefs);
  }, [currentContext, childrenRefs]);
  useClickAway({
    callback,
    childrenRefs: useRef(currentContext.childrenRefs)
  });
  return /*#__PURE__*/React.createElement(ClickAwayAncestorContext.Provider, {
    value: currentContext
  }, children);
};
ClickAwayProvider.displayName = 'ClickAwayProvider';

export { ClickAwayProvider as C };
//# sourceMappingURL=ClickAwayProvider.js.map
